import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import isTomorrow from 'date-fns/esm/isTomorrow';
import isToday from 'date-fns/esm/isToday';
import clsx from 'clsx';
import { NotificationEntity } from '@interpals/api-web';
import reactStringReplace from 'react-string-replace';
import { useSelector } from 'react-redux';
import isYesterday from 'date-fns/esm/isYesterday';

import UserAvatar from 'components/UserAvatar';
import { format, formatDistanceToNowStrict, parseDateStringAsLocal } from 'utils/formatDate';
import ROUTES, { makeAlbumPhotoURL, makeProfileURL, makeSinglePostURL } from 'routes';
import { notificationIcons } from 'constants/notificationIcons';
import Typography from 'components/ui/Typography';
import Image from 'components/Image';
import { Color } from 'types/colors';
import { selectAccount } from 'redux/account/selectors';
import { ContextError } from 'errors/ContextError';
import { SpriteIcon } from 'types/icons';

import css from './Notifications.module.scss';

interface Props {
    notifications: NotificationEntity[];
}

const Notifications: React.FC<Props> = ({ notifications }) => {
    const { t } = useTranslation();

    const account = useSelector(selectAccount);
    if (!account) {
        throw new ContextError('Missing account', { account });
    }

    const getBirthdayI18nKey = (birthday: Date) => {
        if (isTomorrow(birthday)) {
            return 'AlertsMenu.FriendBirthdayTomorrow';
        } else if (isYesterday(birthday)) {
            return 'AlertsMenu.FriendBirthdayYesterday';
        } else if (isToday(birthday)) {
            return 'AlertsMenu.FriendBirthdayToday';
        }

        return 'AlertsMenu.FriendBirthday';
    };

    // Return a single link for each notification, anywhere we click/tap
    // on the notification will take us to this target
    // (This is simpler for UX than making the avatar and nested links
    // point to different locations)
    const getNotificationLink = (notification: NotificationEntity) => {
        if (notification.photo) {
            return makeAlbumPhotoURL({
                username: account.username,
                uid: notification.photo.uid,
                photoId: notification.entity.id,
                albumId: notification.photo.aid,
            });
        } else if (notification.post?.id) {
            return makeSinglePostURL(notification.post.feed_owner_id, notification.post.id);
        }

        switch (notification.type) {
            case 'FRIEND_BIRTHDAY':
                return makeProfileURL(notification.user.name);

            case 'FRIEND_ACCEPT_MESSAGE':
                return ROUTES.FRIENDS;

            case 'FRIEND_REQUEST_MESSAGE':
                return ROUTES.FRIENDS_REQUEST;

            case 'FEED_NEW_POST':
                return makeProfileURL(account.username);

            default:
                return notification.user.username ? makeProfileURL(notification.user.username) : null;
        }
    };

    const typeToI18nKeyMap: { [key: string]: string } = {
        FRIEND_REQUEST_MESSAGE: 'AlertsMenu.IncomingFriendRequest',
        FRIEND_ACCEPT_MESSAGE: 'AlertsMenu.FriendAccept',
        FRIEND_BIRTHDAY: 'AlertsMenu.FriendBirthday',
        LIKE_PHOTO: 'AlertsMenu.Like.Photo',
        LIKE_COMMENT: 'AlertsMenu.Like.Comment',
        LIKE_POST: 'AlertsMenu.Like.Post',
        LIKE_ALBUM: 'AlertsMenu.Like.Album',
        LIKE_TRIP: 'AlertsMenu.Like.Trip',
        LIKE_ENTRY: 'AlertsMenu.Like.Entry',
        COMMENT_PHOTO: 'AlertsMenu.Comment.Photo',
        COMMENT_POST: 'AlertsMenu.Comment.Post',
        COMMENT_ALBUM: 'AlertsMenu.Comment.Album',
        COMMENT_TRIP: 'AlertsMenu.Comment.Trip',
        COMMENT_REPLY: 'AlertsMenu.Comment.Reply',
    };

    const renderNotificationBody = (notification: NotificationEntity) => {
        const notificationUserSex = notification.user.sex ?? 'male';

        let i18nKey;
        const i18nVars: { [key: string]: string } = {
            // Use [[ ]] to not conflict with i18next's {{ }} interpolation syntax
            user: '[[user]]',
            context: notificationUserSex,
        };

        // Birthday notifications are the only ones with multiple i18n keys
        if (notification.type === 'FRIEND_BIRTHDAY') {
            // Not sure if this is the most efficient way to do this, but
            // we want the UTC date (month/day) to be preserved regardless of
            // the user's local time zone
            const birthday = parseDateStringAsLocal(notification.created.toString());

            i18nKey = getBirthdayI18nKey(birthday);
            i18nVars['when'] = format(birthday, 'MMM dd');
        } else {
            i18nKey = typeToI18nKeyMap[notification.type];
        }

        return reactStringReplace(t(i18nKey, i18nVars), '[[user]]', () => (
            <Typography style="BodyMedSemibold" tag="span">
                {notification?.user.name ?? t('Common.InactiveUser')}
            </Typography>
        ));
    };

    return (
        <div>
            {notifications &&
                notifications.map((notification) => (
                    <Link
                        to={getNotificationLink(notification) ?? ''}
                        key={notification.id}
                        className={clsx(css.row, notification.uread && css.unread)}
                    >
                        {/* Avatar: User profile link */}
                        <div>
                            <UserAvatar
                                user={notification.user}
                                size="M"
                                badgeIcon={
                                    notificationIcons.find((item) => item.notificationType === notification.type)
                                        ?.icon as SpriteIcon
                                }
                                badgeColor={
                                    notificationIcons.find((item) => item.notificationType === notification.type)
                                        ?.color as Color
                                }
                            />
                        </div>
                        {/* Notification text + timestamp + thumbnail */}
                        <div className={css.info}>
                            <div className={css.column}>
                                {/* Main notification text */}
                                <Typography>{renderNotificationBody(notification)}</Typography>
                                {/* Timestamp */}
                                <Typography color="gray2">
                                    {formatDistanceToNowStrict(new Date(notification.created), {
                                        addSuffix: true,
                                    })}
                                </Typography>
                            </div>
                            {/* Photo thumbnail */}
                            {notification?.photo && (
                                <div>
                                    <div className={css.photo}>
                                        <Image src={notification.entity.url} circled={false} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Link>
                ))}
        </div>
    );
};

export default React.memo(Notifications);
